/*
 *   File : manage-store.js
 *   Author : https://evoqins.com
 *   Description : Edit store modal component for Super Admin
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { memo, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

// Custom component
import { CustomSelectBox, CustomTextInput } from '../FormElements';
import { PrimaryButton, TextButton } from '../Buttons';
import { Icon } from '../Icon';
import { APIService } from '../../Service';

const ManageStoreModal = (props) => {

    const [storeName, setStoreName] = useState("");
    const [storeNameError, setStoreNameError] = useState("");

    const [storeNameAr, setStoreNameAr] = useState("");

    const [storeCode, setStoreCode] = useState("");
    const [storeCodeError, setStoreCodeError] = useState("");

    const [countryOptions, setCountryOptions] = useState([]);
    const [countryLoader, setCountryLoader] = useState(true);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedCountryError, setSelectedCountryError] = useState("");

    const [city, setCity] = useState("");
    const [cityError, setCityError] = useState("");

    const [phone, setPhone] = useState("");
    const [phoneError, setPhoneError] = useState("");

    const [locationUrl, setLocationUrl] = useState("");
    const [locationUrlError, setLocationUrlError] = useState("");

    const [loader, setLoader] = useState(false);

    useEffect(() => {
        _getFilterData();
    }, []);

    useEffect(() => {
        if (props.editData && Object.keys(props.editData).length !== 0 && countryOptions.length !== 0) {
            const data = props.editData;

            setStoreName(data.name || "");
            setStoreNameAr(data.name_arabic || "");
            setStoreCode(data.store_id || "");
            setCity(data.location || "");
            setPhone(data.mobile || "");
            setLocationUrl(data.location_url || "");

            const country_id = data.country_id ?? null;
            const country_name = data.country_name || "";

            if (country_id !== null) {
                const matched = countryOptions.find((item) => item.value === country_id);
                setSelectedCountry(matched || { value: country_id, label: country_name });
            } else if (country_name) {
                const matched = countryOptions.find((item) => item.label === country_name);
                setSelectedCountry(matched || null);
            } else {
                setSelectedCountry(null);
            }
        }
    }, [props.editData, countryOptions]);

    useEffect(() => {
        setStoreNameError("");
    }, [storeName]);

    useEffect(() => {
        setStoreCodeError("");
    }, [storeCode]);

    useEffect(() => {
        setSelectedCountryError("");
    }, [selectedCountry]);

    useEffect(() => {
        setCityError("");
    }, [city]);

    useEffect(() => {
        setPhoneError("");
    }, [phone]);

    useEffect(() => {
        setLocationUrlError("");
    }, [locationUrl]);

    // handle close modal
    const _handleCloseModal = () => {
        var button = document.getElementById("manage-store-modal-close");
        button.click();
        props.handleClose();
    };

    // handle submit
    const _handleSubmit = () => {
        let is_valid = true;

        if (storeName.trim().length === 0) {
            setStoreNameError("Store name is required");
            is_valid = false;
        }

        if (storeCode.trim().length === 0) {
            setStoreCodeError("Store code is required");
            is_valid = false;
        }

        if (selectedCountry === null) {
            setSelectedCountryError("Country is required");
            is_valid = false;
        }

        if (city.trim().length === 0) {
            setCityError("City is required");
            is_valid = false;
        }

        if (phone.trim().length === 0) {
            setPhoneError("Store phone is required");
            is_valid = false;
        }

        if (locationUrl.trim().length === 0) {
            setLocationUrlError("Store location url is required");
            is_valid = false;
        }

        if (is_valid === true) {
            _editStore();
        }
    };

    // API - Get country filter data
    const _getFilterData = () => {
        setCountryLoader(true);

        let is_post = false;
        let url = "store/filter-data";
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const countries = (response.data || []).map((item) => ({
                    value: item.country_id,
                    label: item.country_name
                }));
                setCountryOptions(countries);
            } else {
                setCountryOptions([]);
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setCountryLoader(false);
        });
    };

    // API - Edit store
    const _editStore = () => {
        setLoader(true);

        let is_post = true;
        let url = 'store/edit';
        let data = {
            "id": props.editData?.id,
            "store_id": storeCode,
            "name": storeName,
            "name_arabic": storeNameAr,
            "country_id": selectedCountry?.value || null,
            "location": city,
            "mobile": phone,
            "location_url": locationUrl
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                toast.dismiss();
                toast.success("Successfully updated store", {
                    type: "success",
                    className: "e-toast",
                });
                _handleCloseModal();
                props.handleSuccess();
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setLoader(false);
        });
    };

    return (
        <div className="modal fade" id="manage-store-modal" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1" aria-labelledby="manageStoreModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered e-border-radius-12px modal-dialog-scrollable">
                <div className="modal-content e-border-radius-12px w-480px mx-auto overflow-hidden">
                    <button id='manage-store-modal-close' type="button" className="btn-close d-none shadow-none" data-bs-dismiss="modal" aria-label="Close"></button>

                    <div className="modal-header px-4 pt-4 pb-2 border-0 align-items-start">
                        <div>
                            <h4 className="mb-1 e-text-dark-jungle-green e-primary-font-playfair e-font-18 e-font-weight-500 line-height-28px">
                                Edit store
                            </h4>
                            <p className='mb-0 e-text-davys-grey e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px'>
                                Update store details.
                            </p>
                        </div>
                        <Icon icon="close-icon"
                            className='cursor-pointer'
                            size="24px"
                            onPress={_handleCloseModal} />
                    </div>

                    <div className="modal-body px-4 pt-2 pb-2 overflow-x-hidden">
                        <CustomTextInput label="Store name"
                            placeholder="Enter store name"
                            type="text"
                            require
                            value={storeName}
                            error={storeNameError}
                            className="w-100 e-font-16"
                            labelStyle="e-text-raisin-black"
                            handleChange={(e) => setStoreName(e)} />

                        <CustomTextInput
                            label={<>Store name (Arabic) <span className='e-text-davys-grey e-font-weight-400'>Optional</span></>}
                            placeholder="Enter store name in Arabic"
                            type="text"
                            value={storeNameAr}
                            className="w-100 e-font-16"
                            labelStyle="e-font-weight-500 e-text-raisin-black"
                            handleChange={(e) => setStoreNameAr(e)} />

                        <CustomTextInput label="Store code"
                            placeholder="Enter store code"
                            type="text"
                            require
                            value={storeCode}
                            error={storeCodeError}
                            className="w-100 e-font-16"
                            labelStyle="e-font-weight-500 e-text-raisin-black"
                            handleChange={(e) => setStoreCode(e)} />

                        <div className='row g-3 m-0'>
                            <div className='col-6 ps-0 pe-2 m-0'>
                                <CustomSelectBox label="Country"
                                    value={selectedCountry}
                                    options={countryOptions}
                                    loader={countryLoader}
                                    error={selectedCountryError}
                                    className="e-modal-select e-bg-white e-border-radius-8"
                                    padding="11px 16px"
                                    fontSize="16px"
                                    fontWeight="400"
                                    isSearchable={false}
                                    selectChange={(value) => setSelectedCountry(value)}
                                />
                            </div>
                            <div className='col-6 pe-0 ps-2 m-0'>
                                <CustomTextInput label="City"
                                    placeholder="Enter city"
                                    type="text"
                                    require
                                    value={city}
                                    error={cityError}
                                    className="w-100 e-font-16"
                                    labelStyle="e-font-weight-500 e-text-raisin-black"
                                    handleChange={(e) => setCity(e)} />
                            </div>
                        </div>

                        <CustomTextInput label="Store phone"
                            placeholder="Enter store phone"
                            type="text"
                            require
                            value={phone}
                            error={phoneError}
                            className="w-100 e-font-16"
                            labelStyle="e-font-weight-500 e-text-raisin-black"
                            handleChange={(e) => setPhone(e)} />

                        <CustomTextInput label="Store location url"
                            placeholder="Enter location url"
                            type="text"
                            require
                            value={locationUrl}
                            error={locationUrlError}
                            className="w-100 e-font-16"
                            labelStyle="e-font-weight-500 e-text-raisin-black"
                            handleChange={(e) => setLocationUrl(e)} />
                    </div>

                    <div className='modal-footer border-0 pt-1 px-4 pb-4'>
                        <div className='d-flex align-items-center justify-content-end gap-3 w-100'>
                            <TextButton name="Cancel"
                                className="e-text-lust"
                                fontSize="e-font-16"
                                onPress={_handleCloseModal} />
                            <PrimaryButton name="Save & Update"
                                className="e-border-radius-16 px-3 padding-14px-tb e-font-16 line-height-12px"
                                status={loader}
                                onPress={_handleSubmit} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

ManageStoreModal.defaultProps = {
    editData: {},
    handleClose: () => { },
    handleSuccess: () => { },
};

export default memo(ManageStoreModal);
